import { cloneDeep, defaultsDeep, get } from 'lodash'
import { Filter } from '../models/filter.model'
import {
  QueryStringFilter,
  QueryStringFilterOperator,
  QueryStringParams,
} from '../models/query-string.model'

const FILTER_INITIAL_STATE: Partial<Filter> = {
  field: undefined,
  operator: undefined,
  value: undefined,
}

type FilterOperator = ':=' | ':ct'

// Init

export function initFilter(filter: Partial<Filter> = {}): Filter {
  return defaultsDeep(cloneDeep(filter), FILTER_INITIAL_STATE)
}

/**
 * Apply a date range filter to query params
 * @param field - The field name to filter on
 * @param dates - Array of two dates [start, end]
 * @param params - The query params object
 * @returns Updated query params with date range filters
 */
export function applyDateRangeFilter<T extends QueryStringParams>(
  field: string,
  dates: Date[],
  params: T,
): T {
  if (!dates[0] || !dates[1]) return params

  const startOfDay = new Date(dates[0])
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(dates[1])
  endOfDay.setHours(23, 59, 59, 999)

  return {
    ...params,
    [`${field}:ge`]: startOfDay.toISOString(),
    [`${field}:lt`]: endOfDay.toISOString(),
  } as T
}

/**
 * Apply a comparison operator filter to query params
 * @param field - The field name to filter on
 * @param operator - The comparison operator
 * @param value - The value to compare (converted to string if Date)
 * @param params - The query params object
 * @returns Updated query params with comparison filter
 */
export function applyComparisonOperator<T extends QueryStringParams>(
  field: string,
  operator: QueryStringFilterOperator,
  value: unknown,
  params: T,
): T {
  const operatorMap: Record<QueryStringFilterOperator, string> = {
    '=': field,
    '<': `${field}:lt`,
    lt: `${field}:lt`,
    '<=': `${field}:le`,
    le: `${field}:le`,
    '>': `${field}:gt`,
    gt: `${field}:gt`,
    '>=': `${field}:ge`,
    ge: `${field}:ge`,
    contains: `${field}:contains`,
    notcontains: `${field}:notcontains`,
    startswith: `${field}:startswith`,
    endswith: `${field}:endswith`,
    between: field,
  }

  const paramKey = operatorMap[operator]
  if (!paramKey) return params

  // ---------- STRICT DATE DETECTION ----------
  const isISODateString = (val: unknown): val is string => {
    return (
      typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val) // only accept ISO-like dates
    )
  }

  const isRealDate = value instanceof Date || isISODateString(value)

  // ---------- DATE HANDLING ----------
  if (isRealDate) {
    const date = value instanceof Date ? value : new Date(value)

    if (!isNaN(date.getTime())) {
      if (operator === '=') {
        const startOfDay = new Date(date)
        startOfDay.setHours(0, 0, 0, 0)

        const startOfNextDay = new Date(startOfDay)
        startOfNextDay.setDate(startOfNextDay.getDate() + 1)

        return {
          ...params,
          [`${field}:ge`]: startOfDay.toISOString(),
          [`${field}:lt`]: startOfNextDay.toISOString(),
        } as T
      }

      return {
        ...params,
        [paramKey]: date.toISOString(),
      } as T
    }
  }

  // ---------- NON-DATE ----------
  return {
    ...params,
    [paramKey]: value,
  } as T
}

/* export function applyComparisonOperator<T extends QueryStringParams>(
  field: string,
  operator: QueryStringFilterOperator,
  value: unknown,
  params: T,
): T {
  const operatorMap: Record<QueryStringFilterOperator, string> = {
    '=': field,
    '<': `${field}:lt`,
    lt: `${field}:lt`,
    '<=': `${field}:le`,
    le: `${field}:le`,
    '>': `${field}:gt`,
    gt: `${field}:gt`,
    '>=': `${field}:ge`,
    ge: `${field}:ge`,
    contains: `${field}:contains`,
    notcontains: `${field}:notcontains`,
    startswith: `${field}:startswith`,
    endswith: `${field}:endswith`,
    between: field, // Special case, handled separately
  }

  const paramKey = operatorMap[operator]
  if (!paramKey) return params

  // For "equals" with a date: treat as whole day (>= 00:00:00 and < next day 00:00:00)
  if (operator === '=') {
    const date =
      value instanceof Date
        ? value
        : typeof value === 'string'
          ? new Date(value)
          : null
    if (date && !isNaN(date.getTime())) {
      const startOfDay = new Date(date)
      startOfDay.setHours(0, 0, 0, 0)
      const startOfNextDay = new Date(startOfDay)
      startOfNextDay.setDate(startOfNextDay.getDate() + 1)
      return {
        ...params,
        [`${field}:ge`]: startOfDay.toISOString(),
        [`${field}:lt`]: startOfNextDay.toISOString(),
        [`${field}:le`]: startOfNextDay.toISOString(),
        [`${field}:gt`]: startOfNextDay.toISOString(),
      } as T
    }
  }

  // Convert Date to ISO string, otherwise use value as-is
  const processedValue = value instanceof Date ? value.toISOString() : value

  return {
    ...params,
    [paramKey]: processedValue,
  } as T
} */

/**
 * Parse filters from a flat object format into QueryStringFilter format
 * Handles three cases:
 * 1. Keys with colons (e.g., 'createdAt:lt') - parsed as field:operator
 * 2. The 'q' key - passed through as search query
 * 3. Simple keys - treated as equality filters
 *
 * @param params - The base query params
 * @param filters - Flat object of filters (can contain operator syntax in keys)
 * @param filterFn - Function to apply each parsed filter
 * @returns Updated query params
 */
export function parseFilters<T extends QueryStringParams, F extends string>(
  params: T,
  filters: Partial<T>,
  filterFn: (params: T, filter: QueryStringFilter<F>) => T,
): T {
  return Object.entries(filters).reduce<T>((result, [key, value]) => {
    // Skip null/undefined values
    if (value === undefined || value === null) {
      return result
    }

    // Handle search query separately
    if (key === 'q') {
      return { ...result, q: value as string }
    }

    // Parse operator syntax (e.g., 'createdAt:lt' -> field='createdAt', operator='lt')
    if (key.includes(':')) {
      const lastColonIndex = key.lastIndexOf(':')
      const field = key.substring(0, lastColonIndex)
      const operator = key.substring(lastColonIndex + 1)

      return filterFn(result, {
        field: field as F,
        operator: operator as QueryStringFilterOperator,
        value,
      })
    }

    // Default to equality operator
    return filterFn(result, {
      field: key as F,
      operator: '=',
      value,
    })
  }, params)
}

export function matchesFilters<T>(
  item: T,
  filters: Record<string, string>,
  getFieldValue?: (item: T, field: string) => unknown,
): boolean {
  const resolve = getFieldValue ?? ((obj: T, f: string) => get(obj, f))

  return Object.entries(filters).every(([fieldWithOperator, filterValue]) => {
    if (filterValue === '') return true

    const match = fieldWithOperator.match(/^(.+?)(:=|:ct|)?$/)
    const field = match ? match[1] : fieldWithOperator
    const operator: FilterOperator = (match?.[2] as FilterOperator) || ':ct'
    const fieldVal = String(resolve(item, field)).toLowerCase()
    const search = String(filterValue).toLowerCase()

    switch (operator) {
      case ':=':
        return fieldVal === search
      case ':ct':
      default:
        return fieldVal.includes(search)
    }
  })
}

export function filterByFields<T>(
  items: T[],
  filters: Record<string, string>,
  getFieldValue?: (item: T, field: string) => unknown,
): T[] {
  return items.filter((item) => matchesFilters(item, filters, getFieldValue))
}

export function mergeById<T extends { _id: string }>(
  existing: T[] = [],
  incoming: T[] = [],
): T[] {
  const map = new Map(existing.map((item) => [item._id, item]))
  incoming.forEach((item) => map.set(item._id, item))
  return Array.from(map.values())
}

export function convertToNumber(value: string): number {
  return Number(`${value}`.replaceAll(',', '.'))
}
